import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router';
import { AlertCircle } from 'lucide-react';
import {
  apiFetch,
  classifyReportAccessStatus,
  readApiErrorMessage,
  type ReportAccessState,
} from '../utils/apiClient';
import SignInWithTwitchButton from './SignInWithTwitchButton';
import { useAnalysisContext } from '../contexts/AnalysisContext';
import AnalysisStageProgress from './AnalysisStageProgress';
import {
  persistActiveAnalysisId,
  readPersistedAnalysisId,
  resolveActiveAnalysisId,
  logActiveAnalysisIdResolution,
  type ActiveAnalysisRouteState,
} from '../utils/activeAnalysisId';
import {
  resolveAnalysisFailureCopy,
  resolveAnalysisStatusCopy,
  statusProgressSignature,
  type AnalysisStatusFields,
} from '../utils/analysisStatusCopy';

interface AnalysisStatusPayload extends AnalysisStatusFields {
  upload_id: string;
  status: string;
  progress: number;
  message: string;
  timestamp?: string;
}

const POLL_INTERVAL_MS = 5000;
const MAX_CONSECUTIVE_ERRORS = 3;
function analyzingPollLog(event: 'start' | 'tick' | 'skip in-flight' | 'stop') {
  if (process.env.NODE_ENV !== 'development') return;
  console.debug(`[AnalyzingPoll] ${event}`);
}

const Analyzing: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { activeAnalyses, addAnalysis, removeAnalysis } = useAnalysisContext();

  const [status, setStatus] = useState<AnalysisStatusPayload | null>(null);
  const [error, setError] = useState<string>('');
  const [accessState, setAccessState] = useState<ReportAccessState>(null);
  const [staleMs, setStaleMs] = useState(0);

  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const progressSignatureRef = useRef<string | null>(null);
  const progressUnchangedSinceRef = useRef<number | null>(null);

  const routeState = location.state as ActiveAnalysisRouteState;

  const contextIds = useMemo(
    () => activeAnalyses.map((a) => a.id),
    [activeAnalyses]
  );

  const analysisId = useMemo(() => {
    const fromState = routeState?.analysisId?.trim() || null;
    const fromQuery =
      searchParams.get('analysisId')?.trim() ||
      searchParams.get('uploadId')?.trim() ||
      searchParams.get('id')?.trim() ||
      null;
    const fromStorage = readPersistedAnalysisId()?.trim() || null;
    const resolved = resolveActiveAnalysisId({
      routeState,
      searchParams,
      contextAnalysisIds: contextIds,
    });
    logActiveAnalysisIdResolution(resolved, {
      routeStateId: fromState,
      queryId: fromQuery,
      storageId: fromStorage,
      contextId: contextIds[0] ?? null,
    });
    return resolved;
  }, [routeState, searchParams, contextIds]);

  const addAnalysisRef = useRef(addAnalysis);
  const removeAnalysisRef = useRef(removeAnalysis);
  addAnalysisRef.current = addAnalysis;
  removeAnalysisRef.current = removeAnalysis;

  useEffect(() => {
    if (!analysisId) return;

    addAnalysisRef.current(analysisId);

    return () => {
      removeAnalysisRef.current(analysisId);
    };
  }, [analysisId]);

  useEffect(() => {
    if (!analysisId) return;

    persistActiveAnalysisId(analysisId);
    mountedRef.current = true;
    analyzingPollLog('start');

    let intervalId: ReturnType<typeof setInterval> | null = null;
    let staleIntervalId: ReturnType<typeof setInterval> | null = null;
    let abortController: AbortController | null = null;
    let consecutiveErrors = 0;

    progressSignatureRef.current = null;
    progressUnchangedSinceRef.current = null;
    setStaleMs(0);

    const clearPollingInterval = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const clearStaleInterval = () => {
      if (staleIntervalId !== null) {
        clearInterval(staleIntervalId);
        staleIntervalId = null;
      }
    };

    const updateStaleTracking = (data: AnalysisStatusPayload) => {
      const signature = statusProgressSignature(data);
      const now = Date.now();
      if (progressSignatureRef.current !== signature) {
        progressSignatureRef.current = signature;
        progressUnchangedSinceRef.current = now;
        setStaleMs(0);
        return;
      }
      if (progressUnchangedSinceRef.current != null) {
        setStaleMs(now - progressUnchangedSinceRef.current);
      }
    };

    const isTerminalStatus = (value: string) =>
      value === 'completed' || value === 'failed' || value === 'error';

    const poll = async (source: 'immediate' | 'interval') => {
      if (!mountedRef.current || !analysisId) return;

      if (inFlightRef.current) {
        analyzingPollLog('skip in-flight');
        return;
      }

      inFlightRef.current = true;
      abortController?.abort();
      abortController = new AbortController();
      const { signal } = abortController;

      if (source === 'interval') {
        analyzingPollLog('tick');
      }

      try {
        const response = await apiFetch(`/api/status/${analysisId}`, { signal });

        if (!mountedRef.current || signal.aborted) return;

        if (!response.ok) {
          const access = classifyReportAccessStatus(response.status);
          if (access) {
            const message = (await readApiErrorMessage(response)) || '';
            setAccessState(access);
            setError(
              access === 'forbidden'
                ? message || 'This report belongs to another account.'
                : access === 'not_found'
                  ? message || 'Report not found.'
                  : message || 'Please sign in to view this report.'
            );
            clearPollingInterval();
            clearStaleInterval();
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: AnalysisStatusPayload = await response.json();

        if (!mountedRef.current || signal.aborted) return;

        consecutiveErrors = 0;
        setStatus(data);
        setError('');
        updateStaleTracking(data);

        if (isTerminalStatus(data.status)) {
          clearPollingInterval();
          clearStaleInterval();
        }
      } catch (pollError) {
        if (pollError instanceof DOMException && pollError.name === 'AbortError') {
          return;
        }

        if (!mountedRef.current || signal.aborted) return;

        consecutiveErrors += 1;
        console.error('Polling error:', pollError);

        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          setError('Failed to check analysis status');
          clearPollingInterval();
          clearStaleInterval();
        }
      } finally {
        inFlightRef.current = false;
      }
    };

    void poll('immediate');

    intervalId = setInterval(() => {
      void poll('interval');
    }, POLL_INTERVAL_MS);

    staleIntervalId = setInterval(() => {
      if (progressUnchangedSinceRef.current == null) return;
      setStaleMs(Date.now() - progressUnchangedSinceRef.current);
    }, 1000);

    return () => {
      analyzingPollLog('stop');
      mountedRef.current = false;
      clearPollingInterval();
      clearStaleInterval();
      abortController?.abort();
      inFlightRef.current = false;
    };
  }, [analysisId]);

  if (!analysisId) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card text-center py-12">
          <AlertCircle className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white mb-2">
            No active analysis found
          </h1>
          <p className="text-secondary-600 dark:text-secondary-300 mb-8">
            Start a new upload or check your profile for in-progress analyses.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate('/upload')} className="btn-primary">
              Go to Upload
            </button>
            <button onClick={() => navigate('/profile')} className="btn-secondary">
              Go to Profile
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isFailed =
    status?.status === 'failed' ||
    status?.status === 'error' ||
    status?.stage === 'failed';
  const failureCopy = status ? resolveAnalysisFailureCopy(status, error || undefined) : null;

  if (error && !status) {
    const timeoutFailure = accessState ? null : resolveAnalysisFailureCopy({}, error);
    const analyzingReturnTo = analysisId ? `/analyzing/${analysisId}` : '/upload';
    return (
      <div className="max-w-4xl mx-auto">
        <div className="card text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
            {accessState === 'auth'
              ? 'Sign in required'
              : accessState === 'forbidden'
                ? 'Access denied'
                : accessState === 'not_found'
                  ? 'Report not found'
                  : timeoutFailure?.title || 'Analysis Error'}
          </h2>
          <p className="text-secondary-600 dark:text-secondary-300 mb-4">
            {timeoutFailure?.message || error}
          </p>
          {accessState === 'auth' ? (
            <SignInWithTwitchButton returnTo={analyzingReturnTo} />
          ) : (
            <div className="space-x-4">
              <button onClick={() => navigate('/upload')} className="btn-primary">
                Try Again
              </button>
              <button onClick={() => navigate('/profile')} className="btn-secondary">
                Go to Profile
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const stage =
    status?.stage ||
    (status?.status === 'queued' ? 'queued' : status ? 'processing' : undefined);
  const isComplete = status?.status === 'completed' || stage === 'completed';

  const presentation = status
    ? resolveAnalysisStatusCopy(status, { staleMs })
    : null;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="card">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white mb-2">
            {isComplete ? 'Analysis Complete' : isFailed ? 'Analysis Unsuccessful' : 'Analysis In Progress'}
          </h1>
          {!isComplete && !isFailed && (
            <p className="text-secondary-600 dark:text-secondary-300 text-sm">
              Analysis typically takes 2–5 minutes. Status refreshes every 5 seconds.
            </p>
          )}
          <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-2 font-mono">
            ID: {analysisId.substring(0, 8)}…
          </p>
        </div>

        <AnalysisStageProgress
          stage={stage}
          status={status?.status}
          message={status?.message}
          progress={status?.progress}
          errorMessage={status?.error_message}
          failedStep={status?.failed_step}
          headline={presentation?.headline}
          detail={presentation?.detail}
        />

        {!isComplete && !isFailed && presentation?.reassurance && (
          <p className="mt-4 text-sm text-secondary-600 dark:text-secondary-300">
            {presentation.reassurance}
          </p>
        )}

        {!isComplete && !isFailed && presentation?.staleNotice && (
          <div className="mt-4 rounded-lg border border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800/60 px-4 py-3">
            <p className="text-sm font-medium text-secondary-900 dark:text-white">
              {presentation.staleNotice.title}
            </p>
            <p className="text-sm text-secondary-600 dark:text-secondary-300 mt-1">
              {presentation.staleNotice.body}
            </p>
          </div>
        )}

        {isFailed && failureCopy && (
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <button onClick={() => navigate('/upload')} className="btn-primary flex-1">
              Try Again
            </button>
            <button onClick={() => navigate('/profile')} className="btn-secondary flex-1">
              Go to Profile
            </button>
          </div>
        )}

        {status?.timestamp && !isComplete && !isFailed && (
          <p className="mt-4 text-xs text-secondary-500 dark:text-secondary-400">
            Last updated from server
          </p>
        )}

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          {isComplete ? (
            <button
              onClick={() => navigate(`/report/${analysisId}`)}
              className="btn-primary flex-1"
            >
              View Report
            </button>
          ) : !isFailed ? (
            <>
              <button onClick={() => navigate('/profile')} className="btn-primary flex-1">
                Check Progress on Profile
              </button>
              <button onClick={() => navigate('/')} className="btn-secondary flex-1">
                Continue Browsing
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Analyzing;
